import React, { useState } from "react"
import { Link, graphql } from "gatsby"
import Helmet from "react-helmet"
import Header from "../components/header"
import Layout from "../components/layout"
import Details from "../components/details"
import { useShoppingCart } from "use-shopping-cart"
import { documentToReactComponents } from "@contentful/rich-text-react-renderer"
import PLACEHOLDER_IMAGE from "../utils/placeholder"
import { extractPlainText } from "../utils/rich-text"
import Seo from "../components/seo"

export const query = graphql`
  query ProductoByCodigo($codigo: String!) {
    contentfulProducto(codigo: { eq: $codigo }) {
      codigo
      nombre
      precio
      descuento
      descripcion {
        raw
      }
      marca
      categoria
      especificaciones {
        etiqueta
        valor
      }
      imagenes {
        title
        file {
          fileName
          url
        }
      }
    }
  }
`

export default function ProductoPage({ data }) {
  const destino = data?.contentfulProducto
  const { addItem } = useShoppingCart()
  const [isAdded, setIsAdded] = useState(false)

  const handleAddToCart = () => {
    const discount = destino.descuento || 0
    const finalPrice =
      discount > 0
        ? Math.round(destino.precio * (1 - discount / 100) * 100)
        : Math.round(destino.precio * 100)
    const product = {
      id: destino.codigo,
      name: destino.nombre,
      description: destino.nombre || "",
      price: finalPrice,
      image:
        destino.imagenes && destino.imagenes[0]
          ? destino.imagenes[0].file.url
          : "",
      currency: "MXN",
    }
    addItem(product)
    setIsAdded(true)
    setTimeout(() => setIsAdded(false), 1500)
  }

  if (!destino) return null

  const discount = destino.descuento || 0
  const finalPrecio =
    discount > 0
      ? (destino.precio * (1 - discount / 100)).toFixed(2)
      : destino.precio.toFixed(2)

  const siteUrl = (
    process.env.GATSBY_SITE_ROOT ||
    process.env.SITE_URL ||
    ""
  ).replace(/\/$/, "")

  const productPath = `/productos/${destino.codigo}`
  const productImage = destino.imagenes?.[0]?.file?.url || ""
  const metaDescription =
    extractPlainText(destino.descripcion?.raw, 160) ||
    `${destino.nombre} disponible en ${
      process.env.GATSBY_siteCompanyName || "nuestra tienda"
    }.`

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: destino.nombre,
    sku: destino.codigo,
    ...(siteUrl && { url: `${siteUrl}${productPath}` }),
    image: destino.imagenes?.map((img) => img.file.url) || [],
    ...(destino.marca && { brand: { "@type": "Brand", name: destino.marca } }),
    offers: {
      "@type": "Offer",
      priceCurrency: "MXN",
      price: finalPrecio,
      availability: "https://schema.org/InStock",
    },
  }

  const breadcrumbItems = [
    { name: "Inicio", path: "/" },
    ...(destino.categoria
      ? [
          {
            name: destino.categoria,
            path: `/?categoria=${encodeURIComponent(destino.categoria)}`,
          },
        ]
      : []),
    { name: destino.nombre, path: productPath },
  ]

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbItems.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: siteUrl ? `${siteUrl}${item.path}` : item.path,
    })),
  }

  return (
    <Layout>
      <Seo
        title={destino.nombre}
        description={metaDescription}
        path={productPath}
        image={productImage}
        type="product"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      <Header />
      <Details
        title={destino.nombre}
        description={
          destino.descripcion?.raw
            ? documentToReactComponents(JSON.parse(destino.descripcion.raw))
            : null
        }
        badge="Producto"
        precio={destino.precio}
        descuento={destino.descuento}
        image={destino.imagenes?.[0]?.file?.url || PLACEHOLDER_IMAGE}
        images={destino.imagenes}
        marca={destino.marca}
        categoria={destino.categoria}
        especificaciones={destino.especificaciones}
      >
        <button
          className={`DetailActionBtn ${
            isAdded ? "DetailActionBtn--success" : "DetailActionBtn--primary"
          }`}
          onClick={handleAddToCart}
          disabled={isAdded}
        >
          {isAdded ? "Agregado al carrito" : "Agregar al Carrito"}
        </button>
        <Link to="/" className="DetailActionBtn DetailActionBtn--secondary">
          Seguir comprando
        </Link>
      </Details>
    </Layout>
  )
}
